import { Fragment, ReactNode, useMemo, useState } from 'react'
import CustomToolTip from '../../../../../../CommonComponents/CustomToolTip'
import InlineInput from '../../../../../../CommonComponents/InlineInput/InlineInput'
import { ScheduleViewButton } from '../../utils'
import './RightSection.scss'
import ScheduleTaskView from './ScheduleTaskView'
import ScheduleTelecommandView from './ScheduleTelecommandView'
import EmptyPage from '../../../../../custom_components/EmptyPage'
import { Command, CustomScheduleModalFormType, RecentlyEditedTelecommandType, ScheduleData, ScheduleTask } from '../../../../../types'
import { isEmpty } from 'lodash'
import { checkCommonNameValidation, containsWhitespace } from '../../../../../../../utils/commonFunctions'

type ScheduleViewType = 'task_view' | 'telecommand_view'

interface RightSectionProps {
    showAddButton?: CustomScheduleModalFormType
    loading?: boolean
    message: string
    subMessage?: string
    icon: ReactNode
    name: string
    isReadonly?: boolean
    currentTime?: number
    selectedSchedule: ScheduleData | null
    selectedTask: ScheduleTask | null
    selectedTelecommand: Command | null
    recentlyEditedTelecommand?: RecentlyEditedTelecommandType | null
    startTime: null | string
    duration: null | number
    scheduleType?: string
    onMenuClick: (menu: string, view: string, type: string, data: { name: string, scheduleId: string, taskId?: string, commandId?: string }) => void
    onTaskSelect: (view: 'readonly' | 'editable', task: ScheduleTask) => void
    onCommandSelect: (view: 'readonly' | 'editable', command: Command) => void
    onChangeName?: (name: string) => void
    onAddTask?: (task: ScheduleTask) => void
    onAddCommand?: (task: ScheduleTask, command: Command | null) => void
}

const RightSection = ({
    showAddButton,
    onChangeName,
    onMenuClick,
    loading = false,
    message,
    icon,
    name,
    isReadonly = true,
    currentTime,
    selectedSchedule,
    selectedTask,
    selectedTelecommand,
    recentlyEditedTelecommand,
    startTime,
    duration,
    subMessage,
    onTaskSelect,
    onCommandSelect,
    onAddTask,
    onAddCommand,
    scheduleType
}: RightSectionProps) => {
    const [view, setView] = useState<ScheduleViewType>('task_view')
    const allTasks = useMemo(() => selectedSchedule?.scheduledTasks || [], [selectedSchedule])
    const hideRightsectionHeader = useMemo(() => (isReadonly && name === ''), [name, isReadonly])
    const allTelecommands = useMemo(() => {
        return (
            allTasks?.flatMap((task) =>
            (task.commandsList || [])
                ?.filter((command) => !isEmpty(command) || !command)
                .map((command) => ({
                    ...command,
                    taskId: task?.taskId,
                    taskName: task?.taskName
                }))
        ) || []
        );
    }, [allTasks]);

    const onChange = (value: string) => {
        // for validation only
        const trimmedValue = value.trim()

        if (!checkCommonNameValidation(trimmedValue, 26)) return

        // keep original input while typing
        onChangeName?.(value)
    }

    return (
        <div className='right__section'>
            {!hideRightsectionHeader && 
                <div className='right__section__header'>
                    <div className='right__section__header__left'>
                        {isReadonly ? (
                            <CustomToolTip title={isReadonly ? name : ''} disabled={!isReadonly} placement='top-start'>
                                <input type="text"
                                    autoFocus
                                    placeholder='Enter Schedule Title'
                                    className={`right__section__header__title__container ${isReadonly ? 'read__only' : ''}`}
                                    value={name}
                                    disabled={isReadonly}
                                    onChange={(e) => onChange(e.target.value)}
                                    onBlur={(e) => onChange(e.target.value.trim())}
                                />
                            </CustomToolTip>
                        ) : (
                        <InlineInput
                            placeholder='Enter Schedule Title'
                            key="schedule-title"
                            value={name}
                            onChange={(value) => onChange(value)}
                            onBlur={() => onChange(name.trim())}
                            disabled={isReadonly}
                            showTooltip={true}
                            tooltipTitle={name}
                            tooltipPlacement='top-start'
                        />
                        )}
                        <div className='right__section__header__details__container'>
                            <span className='right__section__header__details__container__text'>{!isReadonly ? 'Preview' : 'Schedule'}</span>
                            <div className='right__section__header__left__button__container'>
                                {ScheduleViewButton.map((item) => {
                                    const active = view === item.value
                                    const Icon = item.icon
                                    return (
                                        <CustomToolTip title={item.label} placement='top' key={item.label}>
                                            <button className={`right__section__header__left__button ${active ? 'active' : ''}`}
                                                onClick={() => setView(item.value as ScheduleViewType)}>
                                                <Icon />
                                            </button>
                                        </CustomToolTip>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                    <div className='right__section__header__right'>
                        <div className='right__section__header__right__details__container'>
                            <span className='right__section__header__right__details'>Start Time</span>
                            <span className={`right__section__header__right__details ${(isReadonly && selectedSchedule) && 'value'}`}>{startTime ? startTime : 'YYYY-MM-DD HH:MM:SS UTC'}</span>
                            <div className='devider' />
                            <span className='right__section__header__right__details'>Duration</span>
                            <span className={`right__section__header__right__details ${(isReadonly && selectedSchedule) && 'value'}`}>{duration ? duration : '00'} min</span>
                        </div>
                    </div>
                </div>
            }
            {(!selectedSchedule || loading) &&
                <div className='right__section__body'>
                    <EmptyPage content={message} icon={icon} subContent={subMessage} />
                </div>
            }
            {(!loading && selectedSchedule) &&
                <Fragment>
                    {(view === 'task_view') &&
                        <ScheduleTaskView
                            showAddButton={showAddButton}
                            readonly={isReadonly}
                            currentTime={currentTime}
                            selectedSchedule={selectedSchedule}
                            allTasks={allTasks}
                            selectedTask={selectedTask}
                            selectedTelecommand={selectedTelecommand}
                            recentlyEditedTelecommand={recentlyEditedTelecommand}
                            onMenuClick={onMenuClick}
                            onTaskSelect={onTaskSelect}
                            onCommandSelect={onCommandSelect}
                            onAddTask={onAddTask}
                            onAddCommand={onAddCommand}
                            scheduleType={scheduleType}
                        />
                    }
                    {view === 'telecommand_view' &&
                        <ScheduleTelecommandView
                            showAddButton={showAddButton}
                            readonly={isReadonly}
                            currentTime={currentTime}
                            selectedSchedule={selectedSchedule}
                            allTelecommands={allTelecommands}
                            selectedTask={selectedTask}
                            selectedTelecommand={selectedTelecommand}
                            recentlyEditedTelecommand={recentlyEditedTelecommand}
                            onMenuClick={onMenuClick}
                            onCommandSelect={onCommandSelect}
                            onAddCommand={onAddCommand}
                        />
                    }
                </Fragment>
            }

        </div>
    )
}

export default RightSection
